import TextArea from "../../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import { useState, useEffect, useRef } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { formatDate } from "utils/Common/Date";
import {
  getAdminDrawComments,
  postAdminDrawComment,
} from "services/projectService";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import Button from "Components/Common/Button/Button";
import ProgressBar from "Components/Common/ProgressBar/ProgressBar";

interface Props {
  projectOverview: any;
  loading: boolean;
  projectId?: string;
}

const fmtAmt = (amount?: number | null) =>
  amount != null
    ? `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

function OverviewSkeleton() {
  return (
    <div>
      <Skeleton width={100} height={18} style={{ marginBottom: 6 }} />
      <Skeleton width={240} height={12} style={{ marginBottom: 20 }} />
      <div className="metrics">
        {[1, 2, 3, 4].map((i) => (
          <div className="metric" key={i}>
            <Skeleton width={100} height={12} style={{ marginBottom: 8 }} />
            <Skeleton width={80} height={22} style={{ marginBottom: 6 }} />
            <Skeleton width={120} height={11} />
          </div>
        ))}
      </div>
      <div className="two-col">
        <div className="card">
          <Skeleton width={120} height={14} style={{ marginBottom: 16 }} />
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <Skeleton width={200} height={12} />
              <Skeleton width={80} height={12} />
            </div>
          ))}
        </div>
        <div className="actionItemSec">
          <div className="card">
            <Skeleton width={100} height={14} style={{ marginBottom: 16 }} />
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{ display: "flex", gap: 10, marginBottom: 14 }}
              >
                <Skeleton
                  width={8}
                  height={8}
                  borderRadius="50%"
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <div>
                  <Skeleton
                    width={200}
                    height={12}
                    style={{ marginBottom: 4 }}
                  />
                  <Skeleton width={160} height={11} />
                </div>
              </div>
            ))}
          </div>
          <div className="card scheduleSec">
            <Skeleton width={120} height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="100%" height={12} style={{ marginBottom: 8 }} />
            <Skeleton width="70%" height={12} style={{ marginBottom: 8 }} />
            <Skeleton width="50%" height={11} />
          </div>
        </div>
      </div>
      <div className="card contingencySec">
        <Skeleton width={160} height={14} style={{ marginBottom: 16 }} />
        <div className="contGrid">
          {[1, 2].map((i) => (
            <div className="card" key={i}>
              <Skeleton width={120} height={13} style={{ marginBottom: 12 }} />
              <Skeleton
                width="100%"
                height={8}
                borderRadius={4}
                style={{ marginBottom: 10 }}
              />
              {[1, 2, 3].map((j) => (
                <div
                  key={j}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <Skeleton width={100} height={11} />
                  <Skeleton width={70} height={11} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScheduleNote({ projectOverview }: { projectOverview: any }) {
  const est = projectOverview?.estCompletionDate
    ? new Date(projectOverview?.estCompletionDate)
    : null;
  const contract = projectOverview?.contractDate
    ? new Date(projectOverview?.contractDate)
    : null;
  const actualPct: number =
    projectOverview?.inspection?.cscEstimation != null
      ? Number(projectOverview?.inspection?.cscEstimation)
      : parseFloat(projectOverview?.progressPercentage ?? "0");
  const today = new Date();

  if (!est) return null;

  // Past completion date
  if (today > est) {
    const weeks = Math.round(
      (today.getTime() - est.getTime()) / (7 * 86400000),
    );
    return (
      <div className="flag-note flag-note--warn">
        Project is {weeks} week{weeks !== 1 ? "s" : ""} past the estimated
        completion date.
      </div>
    );
  }

  const daysLeft = Math.round((est.getTime() - today.getTime()) / 86400000);

  // With contract date: compare expected vs actual progress
  if (contract && today > contract) {
    const totalDays = (est.getTime() - contract.getTime()) / 86400000;
    const elapsedDays = (today.getTime() - contract.getTime()) / 86400000;
    const expectedPct = Math.min((elapsedDays / totalDays) * 100, 100);
    const gap = expectedPct - actualPct;

    if (gap <= 0) {
      return (
        <div className="flag-note flag-note--ok">
          Project is on track or ahead of schedule.
        </div>
      );
    }
    const weeksLate = Math.max(1, Math.round((gap / 100) * (totalDays / 7)));
    if (gap > 5) {
      return (
        <div className="flag-note flag-note--warn">
          Project appears to be ~{weeksLate} week{weeksLate !== 1 ? "s" : ""}{" "}
          behind schedule as of last inspection.
        </div>
      );
    }
  }

  // No contract date: warn if close to deadline with low progress
  if (!contract && daysLeft < 60 && actualPct < 80) {
    return (
      <div className="flag-note flag-note--warn">
        {daysLeft} days to estimated completion with {actualPct.toFixed(0)}%
        progress reported.
      </div>
    );
  }

  return null;
}

function DrawComments({
  projectId,
  drawId,
}: {
  projectId: string;
  drawId: string;
}) {
  const [comments, setComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [newComment, setNewComment] = useState("");
  useEffect(() => {
    setLoadingComments(true);
    (
      getAdminDrawComments(projectId, drawId) as Promise<{
        data: any;
        error: any;
      }>
    ).then(({ data, error }) => {
      if (!error && data) setComments(data?.data ?? []);
      setLoadingComments(false);
    });
  }, [projectId, drawId]);

  const handleSend = () => {
    const body = (textareaRef.current?.value ?? "").trim();
    if (!body || sending) return;
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    setSending(true);
    (
      postAdminDrawComment(projectId, drawId, body) as Promise<{
        data: any;
        error: any;
      }>
    ).then(({ data, error }) => {
      if (!error) {
        const optimistic = data?.data ?? {
          body,
          createdAt: new Date().toISOString(),
          author: {
            firstName: storedUser.firstName ?? "",
            lastName: storedUser.lastName ?? "",
            companyName: storedUser.companyName ?? "",
          },
        };
        setComments((prev) => [...prev, optimistic]);
        setModalOpen(false);
        setText("");
      }
      setSending(false);
    });
  };

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isSelf = (c: any) =>
    (c.author?.email && c.author.email === storedUser.email) ||
    `${c.author?.firstName ?? ""} ${c.author?.lastName ?? ""}`.trim() ===
      `${storedUser.firstName ?? ""} ${storedUser.lastName ?? ""}`.trim();

  const initials = (first = "", last = "") =>
    `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";

  return (
    <>
      <div className="card draw-comments">
        <div className="card-title">
          Draw Comments
          <Button
            className="btn btn-sm dc-add-btn"
            onClick={() => setModalOpen(true)}
          >
            + Add
          </Button>
        </div>

        <div className="dc-feed">
          {loadingComments ? (
            [1, 2].map((i) => (
              <div className="dc-msg" key={i}>
                <Skeleton
                  width={36}
                  height={36}
                  borderRadius="50%"
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <Skeleton
                    width={200}
                    height={11}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton
                    width="90%"
                    height={13}
                    style={{ marginBottom: 4 }}
                  />
                  <Skeleton width="70%" height={13} />
                </div>
              </div>
            ))
          ) : comments.length === 0 ? (
            <div className="dc-empty">No comments yet.</div>
          ) : (
            comments.map((c, i) => {
              const first = c.author?.firstName ?? "";
              const last = c.author?.lastName ?? "";
              const org = c.author?.companyName ?? "";
              return (
                <div className="dc-msg" key={c.id ?? i}>
                  <div
                    className="dc-av"
                    style={{
                      background: isSelf(c) ? "var(--amber)" : "var(--b)",
                    }}
                  >
                    {initials(first, last)}
                  </div>
                  <div className="dc-body">
                    <div className="dc-meta">
                      <span className="dc-author">
                        {`${first} ${last}`.trim() || "—"}
                      </span>
                      {org && (
                        <>
                          <span className="dc-dot">·</span>
                          <span className="dc-org">{org}</span>
                        </>
                      )}
                      {c.createdAt && (
                        <>
                          <span className="dc-dot">·</span>
                          <span className="dc-time">
                            {formatDate(c.createdAt, true)}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="dc-text">
                      {c.body ?? c.message ?? c.text}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <CustomModal
        show={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          setNewComment("");
        }}
        title="Add Comment"
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                setNewComment("");
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              className="approve-btn"
              onClick={handleSend}
              disabled={sending || !newComment.trim()}
            >
              {sending ? "Posting…" : "Post Comment"}
            </Button>
          </>
        }
      >
        <p className="modal-sub">
          Your comment will be visible to CSC New England and logged against
          this project.
        </p>
        <TextArea
          className="form-control mt-3"
          placeholder="Write your comment…"
          rows={4}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={sending}
          ref={textareaRef}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>
    </>
  );
}

export default function OverviewSection({
  projectOverview,
  loading,
  projectId,
}: Props) {
  if (loading) return <OverviewSkeleton />;
  const g702 = projectOverview?.g702 ?? null;
  const contingency = projectOverview?.contingency ?? null;
  const drawRequest = projectOverview?.drawRequest ?? null;
  const DRAW_STATUS: Record<string, { cls: string; label: string }> = {
    approved: { cls: "b-active", label: "Approved" },
    in_review: { cls: "b-review", label: "In Review" },
    under_review: { cls: "b-review", label: "In Review" },
    pending: { cls: "b-review", label: "Pending" },
    submitted: { cls: "b-review", label: "Submitted" },
    rejected: { cls: "b-denied", label: "Rejected" },
    denied: { cls: "b-denied", label: "Denied" },
  };
  const drawBadge = drawRequest
    ? (DRAW_STATUS[drawRequest.status] ?? {
        cls: "b-review",
        label: drawRequest.status
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c: string) => c.toUpperCase()),
      })
    : null;

  const metrics = [
    {
      label: "Contract Value",
      val: fmtAmt(
        g702?.contractSumToDate?.amount ??
          projectOverview?.contractValue?.amount,
      ),
      sub: "Original + change orders",
    },
    {
      label: "Total Drawn to Date",
      val: fmtAmt(g702?.totalEarnedLessRetainage?.amount),
      sub:
        "After retainage" +
        (projectOverview?.drawRequest?.drawNumber
          ? ` · ${projectOverview?.drawRequest?.drawNumber} draws`
          : ""),
    },
    {
      label: "Balance to Finish",
      val: fmtAmt(g702?.balanceToFinish?.amount),
      sub: "Including retainage held",
    },
    {
      label: "CSC Estimate",
      val: projectOverview?.inspection?.cscEstimation
        ? `${projectOverview?.inspection.cscEstimation}%`
        : "—",
      sub:
        projectOverview?.inspection?.cscEstimation != null
          ? `vs. GC billed  ${projectOverview?.drawRequest?.gcBilled}%`
          : "",
    },
  ];

  const aiaLines = g702
    ? [
        {
          num: "1",
          label: "Original contract sum",
          val: fmtAmt(g702?.originalContractSum?.amount),
        },
        // {
        //   num: "1a",
        //   label: "SOV Update / Final Contract",
        //   val: fmtAmt(g702?.sovUpdate?.amount),
        // },
        {
          num: "2",
          label: "Net change by change orders",
          val: fmtAmt(g702?.netChangeByChangeOrders?.amount),
        },
        {
          num: "3",
          label: "Contract sum to date",
          val: fmtAmt(g702?.contractSumToDate?.amount),
        },
        {
          num: "4",
          label: "Total completed & stored to date",
          val: fmtAmt(g702?.totalCompletedAndStored?.amount),
        },
        {
          num: "5",
          label: `Retainage${g702?.retainageRate ? ` (${Math.round(g702?.retainageRate)}%)` : ""}`,
          val: fmtAmt(g702?.totalRetainage?.amount),
        },
        {
          num: "6",
          label: "Total earned less retainage",
          val: fmtAmt(g702?.totalEarnedLessRetainage?.amount),
          cls: "blue",
        },
        {
          num: "7",
          label: "Less previous certificates",
          val: fmtAmt(g702?.lessPreviousCertificates?.amount),
        },
        {
          num: "8",
          label: "Current payment due",
          val: fmtAmt(g702?.currentPaymentDue?.amount),
          cls: "green",
          total: true,
        },
        {
          num: "9",
          label: "Balance to finish incl. retainage",
          val: fmtAmt(g702?.balanceToFinish?.amount),
        },
      ]
    : null;

  return (
    <div>
      <h1 className="sectionTitle">Overview</h1>
      <div className="section-sub">Project summary and current status</div>

      <div className="metrics">
        {metrics.map((m) => (
          <div className="metric" key={m.label}>
            <div className="metric-label">{m.label}</div>
            <div className="metric-val">{m.val}</div>
            <div className="metric-sub">{m.sub}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        {aiaLines ? (
          <div className="card">
            <div className="card-title">
              Draw Summary
              {drawBadge && drawRequest && (
                <span className={`badge d-flex ${drawBadge.cls}`}>
                  <div className="bdot" />
                  Draw {drawRequest.drawNumber} · {drawBadge.label}
                </span>
              )}
            </div>
            {aiaLines.map((line) => (
              <div
                className={`aia-line${line.total ? " total" : ""}`}
                key={line.num}
              >
                <span>
                  <span className="aia-lnum">{line.num}</span>
                  <span
                    className="aia-label"
                    style={line.total ? { fontWeight: 600 } : undefined}
                  >
                    {line.label}
                  </span>
                </span>
                <span className={`aia-val${line.cls ? ` ${line.cls}` : ""}`}>
                  {line.val}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div />
        )}

        <div className="actionItemSec">
          <div className="card scheduleSec">
            <div className="card-title">Schedule Status</div>
            <div>
              {projectOverview?.estCompletionDate && (
                <div className="aia-label">
                  Substantial completion target:{" "}
                  <strong>
                    {formatDate(projectOverview?.estCompletionDate, false)}
                  </strong>
                </div>
              )}
              <ScheduleNote projectOverview={projectOverview} />
              <div className="d-flex align-item-center gap-1">
                {projectOverview?.contractDate && (
                  <div className="scheduleMeta">
                    Contract date:{" "}
                    {formatDate(projectOverview?.contractDate, false)}
                  </div>
                )}

                {projectOverview?.inspection?.inspectionDate && (
                  <div className="scheduleMeta">
                    · Last inspection:{" "}
                    {formatDate(
                      projectOverview?.inspection?.inspectionDate,
                      false,
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {contingency && (
        <div className="card contingencySec">
          <div className="card-title">Contingency Tracking</div>
          <div className="contGrid">
            {(["gc", "lender"] as const).map((key) => {
              const cont = contingency[key];
              if (!cont) return null;
              const total = cont.totalAmount?.amount ?? 0;
              const drawn = cont.drawnToDate?.amount ?? 0;
              const remaining = cont.remainingBalance?.amount ?? 0;
              const pct = total > 0 ? Math.min((drawn / total) * 100, 100) : 0;
              return (
                <div className="card" key={key}>
                  <div className="contLabel">
                    {key === "gc" ? "GC" : "Lender"} Contingency
                  </div>
                  <ProgressBar value={pct} />
                  <div className="contRow">
                    <span>Total amount</span>
                    <span>{fmtAmt(total)}</span>
                  </div>
                  <div className="contRow">
                    <span>Drawn to date</span>
                    <span>
                      {fmtAmt(drawn)} ({cont.drawnToDate?.percentage ?? 0}%)
                    </span>
                  </div>
                  <div className="contRow">
                    <span>Remaining balance</span>
                    <span>{fmtAmt(remaining)}</span>
                  </div>
                  <div className="contNote">
                    {cont.usedFor?.length > 0
                      ? `Used for: ${cont.usedFor
                          .map((u: any) =>
                            typeof u === "object"
                              ? (u.itemName ?? u.itemLabel) +
                                ` (Draw ${u.drawNumber})`
                              : u,
                          )
                          .join(", ")}`
                      : "No draws requested to date"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {projectId && drawRequest?.id && (
        <DrawComments projectId={projectId} drawId={drawRequest.id} />
      )}
    </div>
  );
}
